import { FC, useEffect, useState } from 'react'
import {
  Button,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import {
  IGetAllVasCodesForWarehouseResponseAction,
  IGetOverflowTimeslotsResponseAction,
} from 'redux/store/actions/globalAction'
import { VestigingEnum } from 'shared/models/Enum'
import { IGetOverflowTimeslotsFilters } from 'shared/models/IGetOverflowTimeslotsFilters'
import { IKlantGroepModel } from 'shared/models/IKlantGroepModel'
import { IOrderType } from 'shared/models/IOrderType'
import { IOverflowTimeslotModel } from 'shared/models/IOverflowTimeslotModel'
import FormSection from './components/FormSection'
import { BigLabel, Label } from './components/Label'
import ProductControl from './controls/ProductControl'
import DayControl from './controls/DayControl'
import OrderTypeControl from './controls/OrderTypeControl'
import { debounce } from 'lodash'
import { VasModel } from 'shared/models/Vas/VasModel'
import LSDRadioButtonValue from './enums/LSDRadioButtonValue'
import EDDRadioButtonValue from './enums/EDDRadioButtonValue'
import LDDRadioButtonValue from './enums/LDDRadioButtonValue'
import VasCodeCodeControl from './controls/VasCodeCodeControl'
import VasCodeConfigControl from './controls/VasCodeConfigControl'
import { createKlantGroepSchema } from './schemas/KlantGroepSchema'

interface IProps {
  show: boolean
  initialData: IKlantGroepModel | null
  globalVestiging: VestigingEnum
  possibleOrderTypes: IOrderType[]
  isSaving: boolean
  saveErrorMessage: string
  onClose: () => void
  onSave: (klantGroep: Partial<IKlantGroepModel>) => void
  onDelete: () => void
  getOverflowTimeslots: (
    filters: IGetOverflowTimeslotsFilters
  ) => Promise<IGetOverflowTimeslotsResponseAction>
  getAllVasCodesForWarehouse: (
    warehouseId: number
  ) => Promise<IGetAllVasCodesForWarehouseResponseAction>
}

const DetailsModal: FC<IProps> = ({
  show,
  initialData,
  isSaving,
  saveErrorMessage,
  globalVestiging,
  possibleOrderTypes,
  onClose,
  onSave,
  onDelete,
  getOverflowTimeslots,
  getAllVasCodesForWarehouse,
}) => {
  const formMethods = useForm<IKlantGroepModel>({
    defaultValues: initialData,
    mode: 'onSubmit',
  })
  const { register, handleSubmit, watch, reset, setValue } = formMethods

  const [lsdRadioButtonValue, setLsdRadioButtonValue] =
    useState<LSDRadioButtonValue>(() => {
      if (initialData?.aflevertijd) {
        return LSDRadioButtonValue.FIXED_TIME
      } else if (initialData?.doorlooptijdInMinuten >= 0) {
        return LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
      } else if (initialData?.lsdLeadTimeBeforeLDD >= 0) {
        return LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD
      }

      return LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
    })
  const [eddRadioButtonValue, setEddRadioButtonValue] =
    useState<EDDRadioButtonValue>(() => {
      if (initialData?.edd) {
        return EDDRadioButtonValue.FIXED_TIME
      } else if (initialData?.eddLeadTimeAfterESD >= 0) {
        return EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
      }

      return EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
    })
  const [lddRadioButtonValue, setLddRadioButtonValue] =
    useState<LDDRadioButtonValue>(() => {
      if (initialData?.ldd) {
        return LDDRadioButtonValue.FIXED_TIME
      } else if (initialData?.lddLeadTimeAfterEDD >= 0) {
        return LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
      } else if (initialData?.lddLeadTimeAfterLSD >= 0) {
        return LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
      }

      return LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
    })

  const [overflowTimeslots, setOverflowTimeslots] = useState<
    IOverflowTimeslotModel[]
  >([])
  const [overflowTimeslotsLoading, setOverflowTimeslotsLoading] =
    useState(false)
  const [overflowTimeslotsErrorMessage, setOverflowTimeslotsErrorMessage] =
    useState<string>('')

  const [vasCodes, setVasCodes] = useState<VasModel[]>([])
  const [vasCodesLoading, setVasCodesLoading] = useState(false)
  const [vasCodesErrorMessage, setVasCodesErrorMessage] = useState<string>('')
  const [validationErrorMessages, setValidationErrorMessages] = useState<
    string[]
  >([])

  const days = watch('dagen')
  const productType = watch('productType')
  const orderType = watch('orderType')
  const uitersteInkoopTijdstipIndicatie = watch(
    'uitersteInkoopTijdstipIndicatie'
  )
  const ordertijdIndicatie = watch('ordertijdIndicatie')
  const vasCode = watch('vasCode')
  const verrekenIndicatie = watch('verrekenIndicatie')

  // Ordertijd disables the days input, so clear it instead of saving a stale value
  useEffect(() => {
    if (ordertijdIndicatie === 'J') {
      setValue('shipmentDaysBeforeDelivery', 0)
    }
  }, [ordertijdIndicatie, setValue])

  // Fetch overflow timeslots
  useEffect(() => {
    const debouncedFetch = debounce(async () => {
      if (orderType && days && productType && uitersteInkoopTijdstipIndicatie) {
        setOverflowTimeslotsLoading(true)
        setOverflowTimeslotsErrorMessage('')
        const response = await getOverflowTimeslots({
          vestigingCode: globalVestiging,
          ordertype: orderType,
          dagen: days,
          productType: productType,
        })

        if (response.success) {
          setOverflowTimeslots(response.payload)
          if (
            response.payload.find(
              overflowTimeslot =>
                overflowTimeslot.klantGroepId === initialData?.overflowFPTId
            )
          ) {
            setValue('overflowFPTId', initialData?.overflowFPTId)
          } else {
            setValue('overflowFPTId', undefined)
          }
        } else {
          setOverflowTimeslotsErrorMessage(response.errorMessage)
          setOverflowTimeslots([])
          setValue('overflowFPTId', undefined)
        }
        setOverflowTimeslotsLoading(false)
      }
    }, 500)

    debouncedFetch()

    return () => {
      debouncedFetch.cancel()
    }
  }, [
    orderType,
    days,
    productType,
    globalVestiging,
    uitersteInkoopTijdstipIndicatie,
    getOverflowTimeslots,
  ])

  // Fetch VAS codes
  useEffect(() => {
    let isMounted = true

    const fetchVasCodes = async () => {
      if (isMounted) {
        setVasCodesLoading(true)
        setVasCodesErrorMessage('')
      }

      const response = await getAllVasCodesForWarehouse(globalVestiging)

      if (isMounted) {
        if (response.success) {
          setVasCodes(response.payload)
        } else {
          setVasCodesErrorMessage(response.errorMessage)
          setVasCodes([])
        }
        setVasCodesLoading(false)
      }
    }

    fetchVasCodes()

    return () => {
      isMounted = false
    }
  }, [globalVestiging, getAllVasCodesForWarehouse])

  const onSubmit = (values: IKlantGroepModel) => {
    const schema = createKlantGroepSchema(
      lsdRadioButtonValue,
      eddRadioButtonValue,
      lddRadioButtonValue,
      ordertijdIndicatie,
      verrekenIndicatie,
      uitersteInkoopTijdstipIndicatie
    )

    const result = schema.safeParse(values)

    if (!result.success) {
      const errors = result.error.issues.map(
        err => `${err.message} - ${err.path}`
      )
      setValidationErrorMessages(errors)

      return
    }

    setValidationErrorMessages([])

    const klantGroep: Partial<IKlantGroepModel> = {
      vestigingCode: globalVestiging,
      klantGroepId: initialData?.klantGroepId,
      klantGroepCode: values.klantGroepCode,
      klantGroepOmschrijving: values.klantGroepOmschrijving,
      orderType: values.orderType,
      dagen: values.dagen,
      productType: values.productType,
      vrijgaveTijdstip: values.vrijgaveTijdstip,
      ordertijdIndicatie: values.ordertijdIndicatie,
      shipmentDaysBeforeDelivery: values.shipmentDaysBeforeDelivery,
      aflevertijd:
        lsdRadioButtonValue === LSDRadioButtonValue.FIXED_TIME
          ? values.aflevertijd
          : undefined,
      doorlooptijdInMinuten:
        lsdRadioButtonValue === LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
          ? values.doorlooptijdInMinuten
          : undefined,
      lsdLeadTimeBeforeLDD:
        lsdRadioButtonValue === LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD
          ? values.lsdLeadTimeBeforeLDD
          : undefined,
      edd:
        eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME
          ? values.edd
          : undefined,
      eddWorkDaysAfterLSD:
        eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME
          ? values.eddWorkDaysAfterLSD
          : undefined,
      eddLeadTimeAfterESD:
        eddRadioButtonValue === EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
          ? values.eddLeadTimeAfterESD
          : undefined,
      ldd:
        lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME
          ? values.ldd
          : undefined,
      lddLeadTimeAfterEDD:
        lddRadioButtonValue === LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
          ? values.lddLeadTimeAfterEDD
          : undefined,
      lddLeadTimeAfterLSD:
        lddRadioButtonValue === LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
          ? values.lddLeadTimeAfterLSD
          : undefined,
      uitersteInkoopTijdstipIndicatie: values.uitersteInkoopTijdstipIndicatie,
      uitersteInkoopTijdstip: uitersteInkoopTijdstipIndicatie
        ? values.uitersteInkoopTijdstip
        : undefined,
      overflowFPTId: uitersteInkoopTijdstipIndicatie
        ? values.overflowFPTId
        : undefined,
      pilotIndicatie: values.pilotIndicatie,
      tijdvakBeginTijdstip: values.tijdvakBeginTijdstip,
      tijdvakEindTijdstip: values.tijdvakEindTijdstip,
      tijdvakKosten: values.tijdvakKosten,
      vasCode: values.vasCode,
      verrekenIndicatie: values.verrekenIndicatie,
      defaultIndicatie: values.defaultIndicatie,
    }

    onSave(klantGroep)
  }

  const handleDelete = () => {
    if (initialData?.isUsedAsOverflow) {
      setValidationErrorMessages([
        'Dit tijdvak is gekoppeld aan een ander tijdvak. Ontkoppel eerst, daarna kan dit tijdvak verwijderd worden.',
      ])
    } else {
      onDelete()
    }
  }

  return (
    <Modal
      show={show}
      onHide={() => {
        reset()
        onClose()
      }}
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Tijdvak Detail</Modal.Title>
      </Modal.Header>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            {initialData?.isUsedAsOverflow && (
              <FormSection>
                <div className='row'>
                  <div className='col-12'>
                    Let op: dit tijdvak wordt gebruikt als overloop tijdvak
                  </div>
                </div>
              </FormSection>
            )}
            <fieldset disabled={isSaving}>
              <FormSection>
                <div className='row'>
                  <div className='col-2'>
                    <h5>Tijdvakgegevens</h5>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-5'>
                    <Label>Code</Label>
                  </div>
                  <div className='col-7'>
                    <input
                      title='Code'
                      type='number'
                      min={1}
                      max={99999}
                      disabled={!!initialData?.klantGroepCode}
                      {...register('klantGroepCode', { valueAsNumber: true })}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-5'>
                    <Label>Vestiging</Label>
                  </div>
                  <div className='col-7'>
                    {initialData?.vestigingCode
                      ? VestigingEnum[initialData.vestigingCode]
                      : VestigingEnum[globalVestiging]}
                  </div>
                </div>
                <div className='row'>
                  <div className='col-5'>
                    <Label>Omschrijving</Label>
                  </div>
                  <div className='col-7'>
                    {
                      <input
                        title='Omschrijving'
                        maxLength={40}
                        className='inputlang'
                        {...register('klantGroepOmschrijving')}
                      />
                    }
                  </div>
                </div>
                <div className='row'>
                  <div className='col-5'>
                    <Label>Default</Label>
                  </div>
                  <div className='col-7'>
                    {initialData?.defaultIndicatie === 'J' ? 'Ja' : 'Nee'}
                  </div>
                </div>
              </FormSection>
              <FormSection>
                <div className='row'>
                  <div className='col-5'>
                    <h5>OrderTypes</h5>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-7 offset-5'>
                    <OrderTypeControl
                      possibleOrderTypes={possibleOrderTypes}
                      disabled={initialData?.isUsedAsOverflow}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-12'>
                    {initialData?.isUsedAsOverflow && (
                      <div className='text-danger'>
                        Dit tijdvak is gekoppeld aan een ander tijdvak.
                        Ontkoppel eerst, daarna kan dit tijdvak gewijzigd
                        worden.
                      </div>
                    )}
                  </div>
                </div>
              </FormSection>
              <FormSection>
                <div className='row'>
                  <div className='col-5'>
                    <h5>Dagen</h5>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-7 offset-5'>
                    <DayControl disabled={initialData?.isUsedAsOverflow} />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-12'>
                    {initialData?.isUsedAsOverflow && (
                      <div className='text-danger'>
                        Dit tijdvak is gekoppeld aan een ander tijdvak.
                        Ontkoppel eerst, daarna kan dit tijdvak gewijzigd
                        worden.
                      </div>
                    )}
                  </div>
                </div>
              </FormSection>
              <FormSection>
                <div className='row'>
                  <div className='col-5'>
                    <h5>Producten</h5>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-7 offset-5'>
                    <ProductControl disabled={initialData?.isUsedAsOverflow} />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-12'>
                    {initialData?.isUsedAsOverflow && (
                      <div className='text-danger'>
                        Dit tijdvak is gekoppeld aan een ander tijdvak.
                        Ontkoppel eerst, daarna kan dit tijdvak gewijzigd
                        worden.
                      </div>
                    )}
                  </div>
                </div>
              </FormSection>
              <FormSection>
                <div className='row'>
                  <div className='col-4'>
                    <BigLabel>EarlyShipment (ESD)</BigLabel>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_esd'
                      value='N'
                      checked={ordertijdIndicatie !== 'J'}
                      {...register('ordertijdIndicatie')}
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Vrijgavetijdstip'
                      type='time'
                      disabled={ordertijdIndicatie === 'J'}
                      {...register('vrijgaveTijdstip')}
                    />
                    {' min '}
                    <input
                      title='Aantal dagen voor de leverdatum waarop ESD valt'
                      type='number'
                      min={0}
                      max={9}
                      step={1}
                      disabled={ordertijdIndicatie === 'J'}
                      {...register('shipmentDaysBeforeDelivery', {
                        valueAsNumber: true,
                      })}
                    />
                    {' dg '}
                    <OverlayTrigger
                      overlay={
                        <Tooltip id='tooltip_shipment_days'>
                          Gebruik de &apos;dag&apos;-instelling om een dag
                          eerder te orderpicken. Controleer ook of de
                          instellingen voor EDD en de marge voor de
                          VAS-begintijd correct zijn.
                        </Tooltip>
                      }
                    >
                      <span className='text-secondary'>&#x1F6C8;</span>
                    </OverlayTrigger>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <Label htmlFor='box_ordertijd'>Ordertijd</Label>
                  </div>
                  <div className='col-1'>
                    <input
                      id='box_ordertijd'
                      type='radio'
                      value='J'
                      checked={ordertijdIndicatie === 'J'}
                      {...register('ordertijdIndicatie')}
                    />
                  </div>
                </div>

                <br />

                <div className='row'>
                  <div className='col-4'>
                    <BigLabel htmlFor='box_lsd'>LatestShipment (LSD)</BigLabel>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_lsd'
                      checked={
                        lsdRadioButtonValue === LSDRadioButtonValue.FIXED_TIME
                      }
                      onChange={() =>
                        setLsdRadioButtonValue(LSDRadioButtonValue.FIXED_TIME)
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Aflevertijd'
                      type='time'
                      disabled={
                        lsdRadioButtonValue !== LSDRadioButtonValue.FIXED_TIME
                      }
                      {...register('aflevertijd')}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <div className='text-right'>
                      <Label htmlFor='box_lsd_lead_time_after_esd'>
                        Doorlooptijd na ESD
                      </Label>
                    </div>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_lsd_lead_time_after_esd'
                      checked={
                        lsdRadioButtonValue ===
                        LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
                      }
                      onChange={() =>
                        setLsdRadioButtonValue(
                          LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
                        )
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Doorlooptijd na ESD'
                      type='number'
                      disabled={
                        lsdRadioButtonValue !==
                        LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
                      }
                      {...register('doorlooptijdInMinuten', {
                        valueAsNumber: true,
                      })}
                    />
                    {' Minuten'}
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <div className='text-right'>
                      <OverlayTrigger
                        overlay={<Tooltip id={''}>Norm aflevertijd</Tooltip>}
                      >
                        <Label htmlFor='box_lsd_lead_time_before_ldd'>
                          Doorlooptijd voor LDD
                          <span className='text-secondary'> &#x1F6C8;</span>
                        </Label>
                      </OverlayTrigger>
                    </div>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_lsd_lead_time_before_ldd'
                      checked={
                        lsdRadioButtonValue ===
                        LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD
                      }
                      onChange={() =>
                        setLsdRadioButtonValue(
                          LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD
                        )
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Doorlooptijd voor LDD'
                      type='number'
                      disabled={
                        lsdRadioButtonValue !==
                        LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD
                      }
                      {...register('lsdLeadTimeBeforeLDD', {
                        valueAsNumber: true,
                      })}
                    />
                    {' Minuten'}
                  </div>
                </div>

                <br />

                <div className='row'>
                  <div className='col-4'>
                    <BigLabel>EarlyDelivery (EDD)</BigLabel>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_edd'
                      checked={
                        eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME
                      }
                      onChange={() =>
                        setEddRadioButtonValue(EDDRadioButtonValue.FIXED_TIME)
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <div>
                      <input
                        title='EarlyDelivery (EDD)'
                        type='time'
                        disabled={
                          eddRadioButtonValue !== EDDRadioButtonValue.FIXED_TIME
                        }
                        {...register('edd')}
                      />
                      {' + '}
                      <input
                        title='Aantal werkdagen na LSD'
                        type='number'
                        max={99}
                        min={0}
                        step={1}
                        disabled={
                          eddRadioButtonValue !== EDDRadioButtonValue.FIXED_TIME
                        }
                        {...register('eddWorkDaysAfterLSD', {
                          valueAsNumber: true,
                        })}
                      />
                      {' Werkdagen na LSD'}
                    </div>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <Label htmlFor='box_edd_lead_time_after_esd'>
                      Doorlooptijd na ESD
                    </Label>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_edd_lead_time_after_esd'
                      checked={
                        eddRadioButtonValue ===
                        EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
                      }
                      onChange={() =>
                        setEddRadioButtonValue(
                          EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
                        )
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Doorlooptijd na ESD'
                      type='number'
                      disabled={
                        eddRadioButtonValue !==
                        EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
                      }
                      {...register('eddLeadTimeAfterESD', {
                        valueAsNumber: true,
                      })}
                    />
                    {' Minuten'}
                  </div>
                </div>

                <br />

                <div className='row'>
                  <div className='col-4'>
                    <BigLabel>LatestDelivery (LDD)</BigLabel>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_ldd'
                      checked={
                        lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME
                      }
                      onChange={() =>
                        setLddRadioButtonValue(LDDRadioButtonValue.FIXED_TIME)
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='LatestDelivery (LDD)'
                      type='time'
                      disabled={
                        lddRadioButtonValue !== LDDRadioButtonValue.FIXED_TIME
                      }
                      {...register('ldd')}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <Label htmlFor='box_ldd_lead_time_after_edd'>
                      Doorlooptijd na EDD
                    </Label>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_ldd_lead_time_after_edd'
                      checked={
                        lddRadioButtonValue ===
                        LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
                      }
                      onChange={() =>
                        setLddRadioButtonValue(
                          LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
                        )
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Doorlooptijd na EDD'
                      type='number'
                      disabled={
                        lddRadioButtonValue !==
                        LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
                      }
                      {...register('lddLeadTimeAfterEDD', {
                        valueAsNumber: true,
                      })}
                    />
                    {' Minuten'}
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <Label htmlFor='box_ldd_lead_time_after_lsd'>
                      Doorlooptijd na LSD
                    </Label>
                  </div>
                  <div className='col-1'>
                    <input
                      type='radio'
                      id='box_ldd_lead_time_after_lsd'
                      checked={
                        lddRadioButtonValue ===
                        LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
                      }
                      onChange={() =>
                        setLddRadioButtonValue(
                          LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
                        )
                      }
                    />
                  </div>
                  <div className='col-7'>
                    <input
                      title='Doorlooptijd na LSD'
                      type='number'
                      disabled={
                        lddRadioButtonValue !==
                        LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
                      }
                      {...register('lddLeadTimeAfterLSD', {
                        valueAsNumber: true,
                      })}
                    />
                    {' Minuten'}
                  </div>
                </div>

                <br />

                {initialData?.defaultIndicatie !== 'J' && (
                  <>
                    <div className='row'>
                      <div className='col-4'>
                        <BigLabel>Final Purchasing Time</BigLabel>
                      </div>
                      <div className='col-1'>
                        <input
                          type='checkbox'
                          id='box_fpt'
                          {...register('uitersteInkoopTijdstipIndicatie')}
                        />
                      </div>
                      <div className='col-7'>
                        <input
                          title='Final Purchasing Time'
                          type='time'
                          disabled={!uitersteInkoopTijdstipIndicatie}
                          {...register('uitersteInkoopTijdstip')}
                        />
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-4'>
                        <Label htmlFor='box_fpt_lead_time_after_ldd'>
                          Overloop tijdvak
                        </Label>
                      </div>
                      <div className='col-1'></div>
                      <div className='col-7'>
                        <select
                          disabled={
                            !uitersteInkoopTijdstipIndicatie ||
                            overflowTimeslotsLoading
                          }
                          style={{ width: '90%' }}
                          itemType='number'
                          {...register('overflowFPTId', {
                            valueAsNumber: true,
                          })}
                        >
                          <option value='' />
                          {overflowTimeslots
                            .filter(
                              overflowTimeslot =>
                                overflowTimeslot.klantGroepId !==
                                initialData?.klantGroepId
                            )
                            .map(overflowTimeslot => (
                              <option
                                key={overflowTimeslot.klantGroepId}
                                value={overflowTimeslot.klantGroepId}
                              >
                                {overflowTimeslot.klantGroepCode}
                                {' - '}
                                {overflowTimeslot.klantGroepOmschrijving}
                              </option>
                            ))}
                        </select>
                        {overflowTimeslotsLoading && (
                          <Spinner
                            animation='border'
                            size='sm'
                            className='ml-2'
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </FormSection>
              <FormSection>
                <div className='row'>
                  <div className='col-12'>
                    <h5>Inkopersscherm configuratie</h5>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-7 offset-5'>
                    <input
                      type='checkbox'
                      id='box_pilot'
                      {...register('pilotIndicatie')}
                    />
                    <label htmlFor='box_pilot' className='ml-1'>
                      Pilot
                    </label>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <Label>Tijdvak</Label>
                  </div>
                  <div className='col-2 offset-1'>Begin:</div>
                  <div className='col-2 offset-1'>Eind:</div>
                </div>
                <div className='row'>
                  <div className='col-2 offset-5'>
                    <input type='time' {...register('tijdvakBeginTijdstip')} />
                  </div>
                  <div className='col-2 offset-1'>
                    <input type='time' {...register('tijdvakEindTijdstip')} />
                  </div>
                </div>
                <br />
                <div className='row'>
                  <div className='col-4'>
                    <Label>Kosten tijdvak</Label>
                  </div>
                  <div className='row'>
                    <div
                      className='col-1 offset-4'
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          marginLeft: '4px',
                          marginTop: '1px',
                        }}
                      >
                        €
                      </span>
                      <input
                        type='number'
                        step='0.01'
                        style={{ paddingLeft: '16px', marginRight: '-8px' }}
                        {...register('tijdvakKosten', { valueAsNumber: true })}
                      />
                    </div>
                  </div>
                </div>
              </FormSection>
              <FormSection>
                <div className='row'>
                  <div className='col-12'>
                    <h5>Value Added Service</h5>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-4'>
                    <Label>Code / Omschrijving</Label>
                  </div>
                  <div className='col-7 offset-1'>
                    <VasCodeCodeControl
                      vasModelList={vasCodes}
                      isLoading={vasCodesLoading}
                    />
                  </div>
                </div>
                <br />
                <div className='row'>
                  <div className='col-10 offset-2'>
                    {vasCodes.find(vc => vc.vasCode === vasCode.code) && (
                      <VasCodeConfigControl />
                    )}
                  </div>
                </div>
              </FormSection>
            </fieldset>
            {(validationErrorMessages.length > 0 ||
              overflowTimeslotsErrorMessage ||
              vasCodesErrorMessage ||
              saveErrorMessage) &&
              [
                ...validationErrorMessages,
                overflowTimeslotsErrorMessage,
                vasCodesErrorMessage,
                saveErrorMessage,
              ]
                .filter(error => error)
                .map((error, index) => (
                  <div className='row' key={index}>
                    <div className='col-12'>
                      <div className='alert alert-danger'>{error}</div>
                    </div>
                  </div>
                ))}
          </Modal.Body>
          <Modal.Footer>
            {initialData?.klantGroepId && (
              <Button
                variant='danger'
                onClick={handleDelete}
                disabled={isSaving}
              >
                Verwijderen
              </Button>
            )}
            <Button
              variant='primary'
              type='submit'
              disabled={isSaving || vasCodesLoading || overflowTimeslotsLoading}
            >
              Opslaan
            </Button>
            <Button variant='secondary' onClick={onClose}>
              Sluiten
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default DetailsModal
